import { isPublicAppPath } from "./publicPaths";
import { signedOutRedirectFor } from "./postAuthPath";

// Keep story, click ids, and utm fields across redirects.
// Auth and checkout params such as next, plan, and step stay on their own URL.
// Copying next would place an external address on a later route.

const PRESERVED_QUERY_KEY = /^(?:story|ref|gclid|gbraid|wbraid|fbclid|msclkid|ttclid)$/;
const UTM_KEY = /^utm_[a-z][a-z0-9_]{0,31}$/;

function normalizedSearch(search) {
  const raw = String(search || "");
  if (!raw) return "";
  return raw.startsWith("?") ? raw.slice(1) : raw;
}

function normalizedHash(hash) {
  const raw = String(hash || "");
  if (!raw) return "";
  return raw.startsWith("#") ? raw : `#${raw}`;
}

function preservedQueryKey(key) {
  const normalized = String(key || "").trim().toLowerCase();
  return PRESERVED_QUERY_KEY.test(normalized) || UTM_KEY.test(normalized);
}

export function withIncomingSearch(path, search = "", hash = "") {
  const base = String(path || "");
  if (!base || /^[a-z][a-z0-9+.-]*:/i.test(base)) return base;
  const incomingRaw = normalizedSearch(search);
  const fragment = normalizedHash(hash);
  const hashAt = base.indexOf("#");
  const withoutHash = hashAt === -1 ? base : base.slice(0, hashAt);
  const baseHash = hashAt === -1 ? "" : base.slice(hashAt);
  const queryAt = withoutHash.indexOf("?");
  const pathname = queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt);
  const params = new URLSearchParams(queryAt === -1 ? "" : withoutHash.slice(queryAt + 1));
  if (incomingRaw) {
    const incoming = new URLSearchParams(incomingRaw);
    incoming.forEach((value, key) => {
      const normalized = String(key || "").trim().toLowerCase();
      if (!value || !preservedQueryKey(normalized) || params.has(normalized)) return;
      params.append(normalized, value);
    });
  }
  const query = params.toString();
  const nextHash = baseHash || fragment;
  return `${pathname}${query ? `?${query}` : ""}${nextHash}`;
}

// Public marketing pages stay put so a background 401 cannot strip the ad query.
// Other pages go to login with the original path and query inside next.
export function hardLoginRedirect(pathname, search = "", hash = "") {
  const path = String(pathname || "");
  if (
    path === "/login"
    || path.startsWith("/login/")
    || path === "/signup"
    || path.startsWith("/signup/")
    || path === "/oauth/callback"
  ) {
    return "";
  }
  if (isPublicAppPath(path)) return "";
  return signedOutRedirectFor(path, search, hash);
}

// www to apex, and /index.html back to /, in one hop. The query and hash stay.
// An apex URL that is already the clean path returns an empty string.
export function indexDocumentRedirect(hostname, pathname, search = "", hash = "") {
  const host = String(hostname || "").toLowerCase();
  const path = String(pathname || "/") || "/";
  const query = normalizedSearch(search);
  const fragment = normalizedHash(hash);
  const suffix = `${query ? `?${query}` : ""}${fragment}`;
  let nextPath = path;
  if (nextPath === "/index.html" || nextPath === "/index.html/") nextPath = "/";
  if (host === "www.dopletech.com") {
    const rooted = nextPath.startsWith("/") ? nextPath : `/${nextPath}`;
    return `https://dopletech.com${rooted}${suffix}`;
  }
  if (nextPath !== path) return `${nextPath}${suffix}`;
  return "";
}
